import { useMemo, useState } from "react";

import { ICalculationData, QuoteType } from "../../../../models/Lead";
import QuoteCalculationBreakdown from "./QuoteCalculationBreakdown";
import {
  buildRowsForSide,
  estimateTotalFromRows,
  getQuoteTypeLabel,
} from "./quoteCalculationUtils";

const BOOKING_QUOTE_TYPES: QuoteType[] = [
  QuoteType.Salvage,
  QuoteType.ScrapExport,
  QuoteType.SmartPrice,
];

const BOOKING_SIDE_TABS = [
  { label: "Collection", isCollection: true },
  { label: "Drop Off", isCollection: false },
];

export default function BookingQuoteCalculationTabs({
  calculationData,
  actualQuoteType,
  actualIsCollection,
  actualTotal,
}: {
  calculationData: ICalculationData;
  actualQuoteType: QuoteType;
  actualIsCollection: boolean;
  actualTotal: number;
}) {
  const [activeQuoteType, setActiveQuoteType] = useState<QuoteType>(actualQuoteType);
  const [activeIsCollection, setActiveIsCollection] = useState<boolean>(actualIsCollection);

  const activeRows = useMemo(
    () => buildRowsForSide(calculationData, activeQuoteType, activeIsCollection),
    [calculationData, activeQuoteType, activeIsCollection]
  );

  const rowsByTypeAndSide = useMemo(() => {
    const map = new Map<string, number>();
    for (let i = 0; i < BOOKING_QUOTE_TYPES.length; i++) {
      const quoteType = BOOKING_QUOTE_TYPES[i];
      for (let j = 0; j < BOOKING_SIDE_TABS.length; j++) {
        const side = BOOKING_SIDE_TABS[j];
        const rows = buildRowsForSide(calculationData, quoteType, side.isCollection);
        map.set(`${quoteType}-${side.isCollection}`, rows.length);
      }
    }
    return map;
  }, [calculationData]);

  const rowsByType = useMemo(() => {
    const map = new Map<QuoteType, number>();
    for (let i = 0; i < BOOKING_QUOTE_TYPES.length; i++) {
      const quoteType = BOOKING_QUOTE_TYPES[i];
      const collectionRows = rowsByTypeAndSide.get(`${quoteType}-true`) ?? 0;
      const dropOffRows = rowsByTypeAndSide.get(`${quoteType}-false`) ?? 0;
      map.set(quoteType, collectionRows + dropOffRows);
    }
    return map;
  }, [rowsByTypeAndSide]);

  const isViewingActual = activeQuoteType === actualQuoteType && activeIsCollection === actualIsCollection;
  const computedTotal = isViewingActual ? actualTotal : estimateTotalFromRows(activeRows);

  return (
    <div className="divide-y divide-gray-200">
      <div className="pb-3">
        <div className="flex flex-wrap gap-2">
          {BOOKING_QUOTE_TYPES.map((quoteType) => {
            const isActive = activeQuoteType === quoteType;
            const isActualType = actualQuoteType === quoteType;
            const hasAnyRowsForType = (rowsByType.get(quoteType) ?? 0) > 0;
            const parentTabClasses = isActualType
              ? "border-emerald-600 text-emerald-700"
              : hasAnyRowsForType
                ? "border-amber-500 text-amber-700"
                : "border-gray-300 text-gray-700";
            const activeParentStyles = isActive ? " bg-slate-100 ring-1 ring-slate-300" : "";
            return (
              <button
                key={quoteType}
                type="button"
                onClick={() => setActiveQuoteType(quoteType)}
                className={`rounded-md border bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-gray-50 ${parentTabClasses}${activeParentStyles}`}
              >
                <span>{getQuoteTypeLabel(quoteType)}</span>
                {isActive && (
                  <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                    Selected
                  </span>
                )}
                {isActualType && (
                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                    Actual
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {BOOKING_SIDE_TABS.map((tab) => {
            const isActive = activeIsCollection === tab.isCollection;
            const isActualTab = actualQuoteType === activeQuoteType && actualIsCollection === tab.isCollection;
            const rowCount = rowsByTypeAndSide.get(`${activeQuoteType}-${tab.isCollection}`) ?? 0;
            const hasRows = rowCount > 0;
            const sideTabClasses = isActualTab
              ? "border-emerald-600 text-emerald-700"
              : hasRows
                ? "border-amber-500 text-amber-700"
                : "border-gray-300 text-gray-700";
            const activeSideStyles = isActive ? " bg-slate-100 ring-1 ring-slate-300" : "";
            return (
              <button
                key={tab.label}
                type="button"
                onClick={() => setActiveIsCollection(tab.isCollection)}
                className={`rounded-md border bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-gray-50 ${sideTabClasses}${activeSideStyles}`}
              >
                <span>{tab.label}</span>
                {isActive && (
                  <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                    Selected
                  </span>
                )}
                {isActualTab && (
                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                    Actual
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="pt-2">
        <QuoteCalculationBreakdown
          calculations={activeRows}
          isCollection={activeIsCollection}
          dropOff={calculationData.dropOff}
          total={computedTotal}
        />
      </div>
    </div>
  );
}
